
import { defineComponent, ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import Dropdown from 'primevue/dropdown';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ActionPayload, MoveToListResult } from './types';
import { useShopLists } from './useShopLists';

export default defineComponent({
  name: 'MoveToListAction',

  components: {
    Dialog,
    Dropdown,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();
    const { lists, loading: loadingLists, fetchLists } = useShopLists();

    // Internal state
    const isVisible = ref(false);
    const selectedList = ref<number | null>(null);
    const currentPayload = ref<ActionPayload | null>(null);

    // Computed header based on context
    const dialogHeader = computed(() => {
      if (currentPayload.value?.context === 'grouped') {
        return t('myLists.actions.move.headerGrouped');
      }
      return t('myLists.actions.move.headerRow');
    });

    /**
     * Open the dialog with the given payload
     */
    const open = async (payload: ActionPayload) => {
      currentPayload.value = payload;
      selectedList.value = null;
      isVisible.value = true;
      // Fetch lists when dialog opens
      await fetchLists(true);
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      selectedList.value = null;
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = () => {
      if (!currentPayload.value || !selectedList.value) return;

      const result: MoveToListResult = {
        context: currentPayload.value.context,
        targetListId: String(selectedList.value),
        selectionInfo: currentPayload.value.selectionInfo,
        rowId: currentPayload.value.rowId,
      };

      emit('confirm', result);
      close();
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      selectedList,
      lists,
      loadingLists,
      dialogHeader,
      close,
      confirm,
    };
  },
});
